import { resourcesVideo } from '@/mocks/resourcesVideo';
import { resourcesSoftware } from '@/mocks/resourcesSoftware';
import { resourcesTutorial } from '@/mocks/resourcesTutorial';
import { categories, subCategoryLabels } from '@/mocks/categories';
import type { Category, Resource } from '@/mocks/types';

export const allResources: Resource[] = [
  ...resourcesVideo,
  ...resourcesSoftware,
  ...resourcesTutorial,
];

export const SITE_NAME = '免费91';

/** Reference "now" for relative date filtering (project demo date). */
export const NOW = new Date('2026-10-14T00:00:00Z').getTime();

export interface ResourceQuery {
  category?: string;
  subCategory?: string;
  keyword?: string;
  timeRange?: string;
  sort?: string;
}

export function getCategoryById(id?: string): Category | undefined {
  return categories.find((item) => item.id === id);
}

export function getResourceById(id?: string): Resource | undefined {
  return allResources.find((item) => item.id === id);
}

export function getSubCategoryLabel(id?: string): string {
  if (!id) return '';
  return subCategoryLabels[id] || id;
}

export function getCategoryName(id?: string): string {
  const found = categories.find((item) => item.id === id);
  return found ? found.name : '资源';
}

const RANGE_DAYS: Record<string, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

export function filterResources(query: ResourceQuery = {}): Resource[] {
  const { category, subCategory, keyword, timeRange, sort } = query;
  const kw = (keyword || '').trim().toLowerCase();
  let list = allResources.slice();

  if (category) {
    list = list.filter((item) => item.category === category);
  }
  if (subCategory) {
    list = list.filter((item) => item.subCategory === subCategory);
  }
  if (kw) {
    list = list.filter((item) => {
      const haystack = [item.title, item.summary, item.author, ...(item.tags || [])]
        .join(' ')
        .toLowerCase();
      return haystack.includes(kw);
    });
  }
  if (timeRange && RANGE_DAYS[timeRange]) {
    const limit = NOW - RANGE_DAYS[timeRange] * 24 * 60 * 60 * 1000;
    list = list.filter((item) => new Date(`${item.publishedAt}T00:00:00Z`).getTime() >= limit);
  }

  if (sort === 'popular') {
    list.sort((a, b) => b.views - a.views);
  } else if (sort === 'favorites') {
    list.sort((a, b) => b.favorites - a.favorites);
  } else {
    list.sort(
      (a, b) =>
        new Date(`${b.publishedAt}T00:00:00Z`).getTime() -
        new Date(`${a.publishedAt}T00:00:00Z`).getTime(),
    );
  }
  return list;
}

export function getLatestResources(count = 6): Resource[] {
  return filterResources({ sort: 'latest' }).slice(0, count);
}

export function getPopularResources(count = 4): Resource[] {
  return filterResources({ sort: 'popular' }).slice(0, count);
}

export function getRelatedResources(resource: Resource, count = 3): Resource[] {
  const sameCategory = allResources.filter(
    (item) => item.category === resource.category && item.id !== resource.id,
  );
  const shuffled = sameCategory
    .map((item) => ({ item, score: Math.random() }))
    .sort((a, b) => a.score - b.score)
    .map((entry) => entry.item);
  return shuffled.slice(0, count);
}

export function getCategoryCount(categoryId: string): number {
  return allResources.filter((item) => item.category === categoryId).length;
}

export function paginate<T>(items: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function totalPages(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

export function formatDate(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate(),
  ).padStart(2, '0')}`;
}

export function formatCount(value: number): string {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}万`;
  return String(value);
}